import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox';
import { useCallback } from 'react';

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextInput,
} from '@/components/Form/inputs';
import { cn } from '@/lib/utils';

import type { PickerSize } from '../pickerPrimitiveStyles';
import ComboboxClear from './ComboboxClear';
import { useComboboxContext } from './ComboboxContext';
import ComboboxIconTrigger from './ComboboxIconTrigger';

const inputGroupButtonSizeClasses: Record<PickerSize, string> = {
  small: cn('size-5 [&_svg]:size-4.5'),
  medium: cn('size-6 [&_svg]:size-5'),
  large: cn('size-7 [&_svg]:size-6'),
};

export type ComboboxInputProps = ComboboxPrimitive.Input.Props & {
  showTrigger?: boolean;
  showClear?: boolean;
  warning?: boolean;
};

const ComboboxInput = ({
  className,
  children,
  showTrigger = true,
  showClear = false,
  warning,
  'aria-invalid': ariaInvalid,
  ...props
}: ComboboxInputProps) => {
  const { size, isLoading, disabled = false, setInputAnchor } = useComboboxContext();
  const setAnchorRef = useCallback(
    (el: HTMLDivElement | null) => {
      setInputAnchor(showTrigger ? el : null);
    },
    [setInputAnchor, showTrigger],
  );

  return (
    <div ref={setAnchorRef} className="w-auto">
      <InputGroup
        className={cn('w-auto', typeof className === 'string' ? className : undefined)}
        size={size}
      >
        <ComboboxPrimitive.Input
          render={<InputGroupTextInput disabled={disabled} warning={warning} size={size} />}
          aria-invalid={ariaInvalid}
          {...props}
        />
        <InputGroupAddon align="inline-end">
          {showTrigger && (
            <InputGroupButton
              className={cn(
                'mr-0.5',
                'group-has-data-[slot=combobox-clear]/input-group:hidden data-pressed:bg-transparent!',
                inputGroupButtonSizeClasses[size],
                isLoading && 'pointer-events-none',
              )}
              data-slot="input-group-button"
              shape="round"
              size={size === 'large' ? 'icon-large' : size === 'medium' ? 'icon' : 'icon-small'}
              variant="ghost"
              render={<ComboboxIconTrigger />}
              disabled={disabled}
            />
          )}
          {showClear && <ComboboxClear disabled={disabled} />}
        </InputGroupAddon>
        {children}
      </InputGroup>
    </div>
  );
};

export default ComboboxInput;
